import React from "react";
import {Box, Text} from "@mantine/core";
import {IconDownload, IconInfoCircle, IconKey, IconSettings} from "@tabler/icons-react";
import classes from "./UserSettingsPage.module.css";
import commonClasses from "../../common/common.module.css";
import cx from "clsx";
import {ApiKeysPanel} from "./settings/ApiKeysPanel";
import {SoftwarePanel} from "@/components/pages/settings/SoftwarePanel.tsx";
import {PreferencesPanel} from "@/components/pages/settings/PreferencesPanel.tsx";
import {AboutPanel} from "@/components/pages/settings/AboutPanel.tsx";
import {useNavigate, useParams} from "react-router";
import {TProtocolDefinitionId} from "@/protocols/types/protocolDefinitions.ts";

export interface UserSettingsPageProps {
}

type SettingsSection = "api-keys" | "software" | "profile" | "notifications" | "preferences" | "about";

interface SidebarItem {
    id: SettingsSection;
    label: string;
    icon: React.ReactNode;
    component: React.ComponentType;
}

const sidebarItems: SidebarItem[] = [
    { id: "preferences", label: "Preferences", icon: <IconSettings size={"1em"}/>, component: PreferencesPanel },
    { id: "software", label: "Software", icon: <IconDownload size={"1em"}/>, component: SoftwarePanel },
    {
        id: "api-keys",
        label: "API Keys",
        icon: <IconKey size={"1em"}/>,
        component: ApiKeysPanel,
    },
    { id: "about", label: "About", icon: <IconInfoCircle size={"1em"}/>, component: AboutPanel },
    // Future sections can be added here:
    // { id: "profile", label: "Profile", icon: <IconUser size={18}/>, component: ProfilePanel },
    // { id: "notifications", label: "Notifications", icon: <IconBell size={18}/>, component: NotificationsPanel },
];

export const UserSettingsPage: React.FC<UserSettingsPageProps> = () => {
    // the url is the single source of truth for the selected section, so the sidebar and
    // the cog menu in the page header stay in sync
    const navigate = useNavigate();
    const selectedSection = (useParams().section ?? "api-keys") as SettingsSection;

    const selectedItem = sidebarItems.find((item) => item.id === selectedSection);
    const SelectedComponent = selectedItem?.component;

    return (
        <Box className={classes.root}>
            <Box className={classes.sidePanel}>
                <Box className={classes.sidebarHeader}>
                    <Text size="lg" fw={700}>Settings</Text>
                </Box>

                <Box className={classes.sidebarItems}>
                    {sidebarItems.map((item) => (
                        <Box
                            key={item.id}
                            onClick={() => navigate(`/settings/${item.id}`)}
                            className={cx(
                                classes.sidebarItem,
                                selectedSection === item.id && classes.sidebarItemActive,
                                commonClasses.hover
                            )}
                        >
                            <Box className={classes.sidebarItemIcon}>
                                {item.icon}
                            </Box>
                            <Text size="sm">{item.label}</Text>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box className={classes.content}>
                <Box className={classes.contentBody}>
                    {SelectedComponent && <SelectedComponent/>}
                </Box>
            </Box>
        </Box>
    );
};

